import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bot, PlugZap, RotateCcw, Users, X } from 'lucide-react';
import {
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
  type TonPastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { useToast } from '../components/ui/useToast';
import {
  CLASSE_CHAMP_IA,
  ModaleActivationIa,
  ModaleQuotaFormation,
  ModaleReglageCompte,
} from '../components/usage-ia/ModalesUsageIa';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import { dateReunion } from '../lib/useFormateurs';
import {
  useReglerModeleIa,
  useReglerOrganismeIa,
  useSupervisionIa,
  type CibleActivation,
  type DesactivationIa,
  type EtatUsageCompte,
  type LigneApprenantIa,
  type LigneCompteIa,
  type LigneFormateurIa,
  type LigneFormationIa,
  type ParFonctionIa,
  type PrestataireIa,
  type RaccordementIa,
  type ReglageIa,
  type ReponseSupervisionIa,
} from '../lib/useUsageIa';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function horodatage(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { timeZone: 'Indian/Reunion', dateStyle: 'short', timeStyle: 'short' });
}

function requetes(nombre: number): string {
  return `${nombre} requête${nombre > 1 ? 's' : ''}`;
}

const TON_ETAT: Record<EtatUsageCompte, TonPastille> = {
  AUTORISE: 'success',
  AU_DELA_DU_SEUIL: 'warning',
  QUOTA_ATTEINT: 'danger',
  NON_AUTORISE: 'neutral',
  DESACTIVE: 'neutral',
};

type FiltreEtat = '' | EtatUsageCompte;

const FILTRES_ETAT_FORMATEUR: { valeur: FiltreEtat; libelle: string }[] = [
  { valeur: '', libelle: 'Tous les états' },
  { valeur: 'NON_AUTORISE', libelle: 'Non autorisé' },
  { valeur: 'AUTORISE', libelle: 'Autorisé' },
  { valeur: 'AU_DELA_DU_SEUIL', libelle: 'Au-delà du seuil' },
  { valeur: 'QUOTA_ATTEINT', libelle: 'Quota atteint' },
  { valeur: 'DESACTIVE', libelle: 'IA désactivée' },
];

// Un apprenant n'a ni seuil d'alerte ni désactivation : son quota l'ouvre ou le ferme.
const FILTRES_ETAT_APPRENANT = FILTRES_ETAT_FORMATEUR.filter(
  (filtre) => filtre.valeur !== 'AU_DELA_DU_SEUIL' && filtre.valeur !== 'DESACTIVE',
);

type Geste =
  | { type: 'activation'; cible: CibleActivation; id: string; nom: string; desactiver: boolean }
  | { type: 'quota'; ligne: LigneCompteIa; formateur?: { seuilPropre: number | null } }
  | { type: 'quotaFormation' };

/**
 * Supervision de l'usage IA (phase 5, TF-439, écran `ai-monitoring` de la
 * maquette ; quotas TF-474). Chaque requête IA passe par la passerelle de
 * l'API, qui la compte — sans garder ni la question ni la réponse — et refuse
 * ce que la direction a coupé. L'IA est fermée de base : la génération et le
 * tuteur ne partent, pour un formateur ou un apprenant, que dans le quota du
 * jour que la direction lui accorde, compte par compte ou aux apprenants en
 * formation d'une formation. L'écran lit les comptes et règle : quotas, seuil
 * d'alerte (organisme et formateur), correction proposée des tests,
 * désactivation d'un formateur ou d'une formation, motivée et tracée.
 *
 * Le « % de contenu généré par l'IA dans les cours » de la maquette n'est pas
 * repris : les cours ne sont pas rédigés dans la plateforme.
 */
export function SupervisionIaPage(): JSX.Element {
  const { site } = usePerimetre();
  const [parametres, setParametres] = useSearchParams();
  const cible = parametres.get('formateur');
  const { data, isLoading, error } = useSupervisionIa(site);
  const [geste, setGeste] = useState<Geste | null>(null);

  const oublierCible = (): void =>
    setParametres(
      (avant) => {
        const suivant = new URLSearchParams(avant);
        suivant.delete('formateur');
        return suivant;
      },
      { replace: true },
    );

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Supervision de l’usage IA"
        sousTitre="L’IA reste un outil d’aide · fermée de base, ouverte compte par compte par un quota du jour"
        actions={<BarreSites />}
      />

      {isLoading && <Chargement libelle="Chargement de l’usage IA…" />}
      {error && <MessageErreur message={messageErreur(error, 'L’usage IA n’a pas pu être chargé.')} />}

      {data && (
        <>
          <BandeauRaccordement raccordement={data.raccordement} />
          <Compteurs data={data} />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="min-w-0 xl:col-span-2">
              <ListeFormateurs data={data} cible={cible} onOublierCible={oublierCible} onGeste={setGeste} />
            </div>
            <div className="min-w-0 space-y-6">
              <CartePrestataire prestataire={data.prestataire} />
              <CarteReglages reglage={data.reglage} />
              <CarteAutresUsages autres={data.autres} fenetre={data.fenetres.mois} />
            </div>
          </div>

          <ListeApprenants data={data} onGeste={setGeste} />

          <ListeFormations formations={data.formations} fenetre={data.fenetres.mois} onGeste={setGeste} />

          <p className="text-[11px] text-codialis-gray">
            Une requête compte dès qu’elle part chez le prestataire, réponse reçue ou non ; les refus sont comptés à
            part. Ni la question ni la réponse ne sont gardées. Les requêtes se conservent {data.conservationAns} ans ·
            calculé le {horodatage(data.calculeLe)}.
          </p>
        </>
      )}

      {data && geste?.type === 'quota' && (
        <ModaleReglageCompte
          ligne={geste.ligne}
          {...(geste.formateur ? { formateur: geste.formateur } : {})}
          reglage={data.reglage}
          onFermer={() => setGeste(null)}
        />
      )}
      {data && geste?.type === 'quotaFormation' && (
        <ModaleQuotaFormation
          formations={data.formations}
          apprenants={data.apprenants}
          site={site}
          reglage={data.reglage}
          onFermer={() => setGeste(null)}
        />
      )}
      {geste?.type === 'activation' && (
        <ModaleActivationIa
          cible={geste.cible}
          id={geste.id}
          nom={geste.nom}
          desactiver={geste.desactiver}
          onFermer={() => setGeste(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raccordement et compteurs
// ---------------------------------------------------------------------------

function BandeauRaccordement({ raccordement }: { raccordement: RaccordementIa[] }): JSX.Element {
  const aucune = raccordement.every((ligne) => !ligne.raccordee);
  return (
    <section
      className={`flex flex-col gap-3 rounded-2xl p-4 shadow-card sm:flex-row sm:items-start sm:p-5 ${
        aucune ? 'border border-amber-200 bg-amber-50' : 'bg-white'
      }`}
    >
      <PlugZap className={`h-5 w-5 shrink-0 ${aucune ? 'text-amber-600' : 'text-codialis-teal'}`} aria-hidden />
      <div className="min-w-0 space-y-2">
        <p className="text-sm font-semibold text-codialis-navy">
          {aucune
            ? 'Aucun prestataire d’IA n’est raccordé : aucune requête ne part ni n’est comptée.'
            : 'Fonctions IA de la plateforme'}
        </p>
        {aucune && (
          <p className="text-xs text-codialis-gray">
            Les quotas et les désactivations réglés ici s’appliqueront dès le raccordement, sans autre geste.
          </p>
        )}
        <ul className="flex flex-wrap gap-2">
          {raccordement.map((ligne) => (
            <li key={ligne.fonction}>
              <Pastille
                texte={`${ligne.libelle} · ${ligne.raccordee ? (ligne.prestataire ?? 'raccordée') : 'non raccordée'}`}
                ton={ligne.raccordee ? 'success' : 'neutral'}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Compteurs({ data }: { data: ReponseSupervisionIa }): JSX.Element {
  const { compteurs } = data;
  const autorises = compteurs.formateursAutorises + compteurs.apprenantsAutorises;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Compteur
        libelle="Requêtes IA aujourd’hui"
        valeur={compteurs.requetesJour}
        ton="blue"
        detail={`${requetes(compteurs.requetesMois)} sur ${data.fenetres.mois} jours · formateurs et apprenants`}
      />
      <Compteur
        libelle="Comptes autorisés"
        valeur={autorises}
        ton={autorises > 0 ? 'navy' : 'warm'}
        detail={`${compteurs.formateursAutorises} formateur${compteurs.formateursAutorises > 1 ? 's' : ''} sur ${compteurs.formateurs} · ${compteurs.apprenantsAutorises} apprenant${compteurs.apprenantsAutorises > 1 ? 's' : ''} sur ${compteurs.apprenants}`}
      />
      <Compteur
        libelle="Quota atteint"
        valeur={compteurs.quotasAtteints}
        ton={compteurs.quotasAtteints > 0 ? 'red' : 'navy'}
        detail={`Aujourd’hui · ${compteurs.auDelaDuSeuil} formateur${compteurs.auDelaDuSeuil > 1 ? 's' : ''} au-delà du seuil`}
      />
      <Compteur
        libelle="Alertes ouvertes"
        valeur={compteurs.alertesOuvertes}
        ton={compteurs.alertesOuvertes > 0 ? 'warm' : 'navy'}
        detail={`${requetes(compteurs.refuseesMois)} refusée${compteurs.refuseesMois > 1 ? 's' : ''} sur ${data.fenetres.mois} jours`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prestataire et modèle
// ---------------------------------------------------------------------------

const ID_AIDE_MODELE = 'aide-modele-ia';

function CartePrestataire({ prestataire }: { prestataire: PrestataireIa }): JSX.Element {
  const toast = useToast();
  const regler = useReglerModeleIa();
  const [modele, setModele] = useState(prestataire.modele ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setModele(prestataire.modele ?? '');
  }, [prestataire.modele]);

  const saisi = modele.trim();
  const inchange = saisi === (prestataire.modele ?? '');
  const retrait = saisi === '' && prestataire.modele !== null;
  const { enVigueur } = prestataire;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(
      { modele: saisi || null },
      {
        onSuccess: (vue) =>
          toast(
            vue.modele
              ? `Modèle d’IA enregistré : ${vue.libelleModele ?? vue.modele}`
              : `Retour au modèle par défaut : ${vue.enVigueur.libelle}`,
          ),
        onError: (refus) => setErreur(messageErreur(refus, 'Le modèle n’a pas pu être enregistré.')),
      },
    );
  };

  return (
    <Carte titre="Prestataire IA" sansBordure>
      <form onSubmit={enregistrer} className="space-y-4 p-4 sm:p-6">
        <p className="text-xs text-codialis-gray">
          {prestataire.nom} sert la génération de contenu, le tuteur et la correction proposée des tests. Ni nom ni
          dossier ne lui sont envoyés.
        </p>
        {!prestataire.cleConfiguree && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            La clé {prestataire.nom} n’est pas posée sur le serveur : le modèle choisi s’appliquera dès qu’elle le
            sera.
          </p>
        )}
        <div>
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Modèle</span>
            <input
              type="text"
              value={modele}
              onChange={(evenement) => setModele(evenement.target.value)}
              placeholder="Vide : modèles gratuits d’OpenRouter"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={200}
              aria-describedby={ID_AIDE_MODELE}
              className={CLASSE_CHAMP_IA}
            />
          </label>
          <p id={ID_AIDE_MODELE} className="mt-1 text-[11px] text-codialis-gray">
            Identifiant copié depuis{' '}
            <a
              href="https://openrouter.ai/models"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-codialis-blue underline"
            >
              openrouter.ai/models
            </a>
            , par exemple mistralai/mistral-small-3.2-24b-instruct. Vide : les modèles gratuits d’OpenRouter,
            limités par OpenRouter (50 requêtes par jour tant que le compte n’a pas acheté de crédits) et de qualité
            variable.
          </p>
        </div>
        {erreur && (
          <p role="alert" className="text-xs font-medium text-red-600">
            {erreur}
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="min-w-0 text-[11px] text-codialis-gray">
            {enVigueur.parDefaut || !prestataire.regleLe
              ? `En vigueur : ${enVigueur.libelle} (${enVigueur.modele}), par défaut.`
              : `En vigueur : ${enVigueur.libelle}, choisi${
                  prestataire.reglePar ? ` par ${prestataire.reglePar}` : ''
                } le ${dateReunion(prestataire.regleLe)}.`}
          </p>
          <button
            type="submit"
            disabled={inchange || regler.isPending}
            className="min-h-[44px] shrink-0 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-50"
          >
            {regler.isPending ? 'Vérification…' : retrait ? 'Revenir au modèle gratuit' : 'Enregistrer le modèle'}
          </button>
        </div>
      </form>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Réglages de l'organisme
// ---------------------------------------------------------------------------

function CarteReglages({ reglage }: { reglage: ReglageIa }): JSX.Element {
  const toast = useToast();
  const regler = useReglerOrganismeIa();
  const [seuil, setSeuil] = useState(String(reglage.seuilAlerteJour));
  const [correction, setCorrection] = useState(reglage.correctionTests);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setSeuil(String(reglage.seuilAlerteJour));
    setCorrection(reglage.correctionTests);
  }, [reglage.seuilAlerteJour, reglage.correctionTests]);

  const inchange = Number(seuil) === reglage.seuilAlerteJour && correction === reglage.correctionTests;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(
      { seuilAlerteJour: Number(seuil), correctionTests: correction },
      {
        onSuccess: () => toast('Réglages de l’usage IA enregistrés'),
        onError: (refus) => setErreur(messageErreur(refus, 'Les réglages n’ont pas pu être enregistrés.')),
      },
    );
  };

  return (
    <Carte titre="Contrôles de la direction" sansBordure>
      <form onSubmit={enregistrer} className="space-y-4 p-4 sm:p-6">
        <p className="text-xs text-codialis-gray">
          L’IA est fermée à chaque formateur et apprenant tant que vous ne lui accordez pas de quota, sur sa ligne ou
          par formation. La direction n’a pas de quota.
        </p>
        <label className="block min-w-0">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Seuil d’alerte
          </span>
          <input
            type="number"
            inputMode="numeric"
            required
            min={reglage.min}
            max={reglage.max}
            step={1}
            value={seuil}
            onChange={(evenement) => setSeuil(evenement.target.value)}
            className={CLASSE_CHAMP_IA}
          />
          <span className="mt-1 block text-[11px] text-codialis-gray">
            Requêtes par jour et par formateur ; au-delà, la direction est alertée. Un formateur peut avoir le sien.
          </span>
        </label>
        <label className="flex items-start gap-3 rounded-xl border border-gray-200 px-3 py-3">
          <input
            type="checkbox"
            checked={correction}
            onChange={(evenement) => setCorrection(evenement.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-codialis-blue [@media(pointer:coarse)]:h-5 [@media(pointer:coarse)]:w-5"
          />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-codialis-navy">Correction proposée des tests</span>
            <span className="block text-[11px] text-codialis-gray">
              L’IA propose une note pour chaque réponse libre d’un test de positionnement, qu’une personne valide.
              Éteinte, elles se notent à la main.
            </span>
          </span>
        </label>
        {erreur && (
          <p role="alert" className="text-xs font-medium text-red-600">
            {erreur}
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] text-codialis-gray">
            {reglage.parDefaut || !reglage.regleLe
              ? 'Valeurs par défaut, jamais réglées.'
              : `Réglés${reglage.reglePar ? ` par ${reglage.reglePar}` : ''} le ${dateReunion(reglage.regleLe)}.`}
          </p>
          <button
            type="submit"
            disabled={inchange || regler.isPending || seuil.trim() === ''}
            className="min-h-[44px] shrink-0 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-50"
          >
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Carte>
  );
}

function CarteAutresUsages({
  autres,
  fenetre,
}: {
  autres: ReponseSupervisionIa['autres'];
  fenetre: number;
}): JSX.Element {
  return (
    <Carte titre={`Autres usages · ${fenetre} jours`}>
      {autres.length === 0 ? (
        <p className="px-4 py-4 text-sm text-codialis-gray sm:px-6">
          Aucune requête de la direction, de transcription ni de correction de test sur la période.
        </p>
      ) : (
        autres.map((ligne) => (
          <div key={ligne.fonction} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <span className="min-w-0 text-sm text-codialis-navy">{ligne.libelle}</span>
            <span className="shrink-0 text-right text-xs text-codialis-gray">
              <span className="font-display text-base font-bold text-codialis-navy">{ligne.envoyees}</span>
              {ligne.refusees > 0 && ` · ${ligne.refusees} refusée${ligne.refusees > 1 ? 's' : ''}`}
            </span>
          </div>
        ))
      )}
      <p className="px-4 py-3 text-[11px] text-codialis-gray sm:px-6">
        Hors quota : la direction, la transcription des vidéos (hébergée chez nous) et la correction proposée des tests,
        comptées par formation.
      </p>
    </Carte>
  );
}

// ---------------------------------------------------------------------------
// Formateurs
// ---------------------------------------------------------------------------

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';
const CLASSE_GESTE =
  'inline-flex min-h-[36px] items-center whitespace-nowrap rounded-lg px-2 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[44px]';

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function ListeFormateurs({
  data,
  cible,
  onOublierCible,
  onGeste,
}: {
  data: ReponseSupervisionIa;
  cible: string | null;
  onOublierCible: () => void;
  onGeste: (geste: Geste) => void;
}): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [etat, setEtat] = useState<FiltreEtat>('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const differee = useValeurDifferee(recherche.trim().toLowerCase(), 200);

  const lignes = useMemo(() => {
    const sansAccent = (texte: string): string => texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const mots = sansAccent(differee).split(/\s+/).filter(Boolean);
    return data.formateurs.filter((ligne) => {
      if (cible) return ligne.utilisateurId === cible;
      if (etat && ligne.etat !== etat) return false;
      const texte = sansAccent(`${ligne.prenom} ${ligne.nom} ${ligne.email}`);
      return mots.every((mot) => texte.includes(mot));
    });
  }, [data.formateurs, cible, etat, differee]);

  useEffect(() => setPage(1), [differee, etat, cible, taillePage]);
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const ciblee = cible ? data.formateurs.find((ligne) => ligne.utilisateurId === cible) : null;

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display font-bold text-codialis-navy">Usage par formateur</h2>
          <span className="text-xs text-codialis-gray">
            Génération et tuteur · seuil d’alerte : {data.reglage.seuilAlerteJour} req./j
          </span>
        </div>
        {cible ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-codialis-navy">
            <span>
              {ciblee
                ? `Formateur ciblé : ${ciblee.prenom} ${ciblee.nom}`
                : 'Ce formateur n’est pas dans le périmètre.'}
            </span>
            <button
              type="button"
              onClick={onOublierCible}
              className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 font-semibold text-codialis-sky hover:underline"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Tous les formateurs
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher un formateur"
              placeholder="Nom, prénom ou e-mail…"
            />
            <ChampFiltre valeur={etat} onChange={(valeur) => setEtat(valeur as FiltreEtat)} etiquette="État">
              {FILTRES_ETAT_FORMATEUR.map((option) => (
                <option key={option.valeur} value={option.valeur}>
                  {option.libelle}
                </option>
              ))}
            </ChampFiltre>
            {(recherche || etat) && (
              <button
                type="button"
                onClick={() => {
                  setRecherche('');
                  setEtat('');
                }}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                Réinitialiser
              </button>
            )}
          </div>
        )}
      </div>

      {lignes.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-codialis-gray">
          {data.formateurs.length === 0
            ? 'Aucun compte formateur dans ce périmètre.'
            : 'Aucun formateur ne correspond aux filtres.'}
        </p>
      ) : (
        <>
          {/* Sous l'ordinateur large, une carte par formateur : le tableau y défilerait de côté. */}
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 2xl:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.utilisateurId} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <IdentiteFormateur ligne={ligne} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Requetes ligne={ligne} />
                  <QuotaCompte ligne={ligne} seuil={ligne} />
                </div>
                <EtatCompte ligne={ligne} alerteOuverte={ligne.alerteOuverte} />
                <GestesFormateur ligne={ligne} onGeste={onGeste} />
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto 2xl:block">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Formateur', 'Requêtes', 'Quota', 'État', ''].map((colonne, index) => (
                    <th key={colonne || index} className={CLASSE_TH}>
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.utilisateurId} className="transition hover:bg-gray-50">
                    <td className={`${CLASSE_TD} max-w-[240px]`}>
                      <IdentiteFormateur ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <Requetes ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <QuotaCompte ligne={ligne} seuil={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[220px]`}>
                      <EtatCompte ligne={ligne} alerteOuverte={ligne.alerteOuverte} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <GestesFormateur ligne={ligne} onGeste={onGeste} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
            total={lignes.length}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="formateur"
          />
        </>
      )}
    </div>
  );
}

function IdentiteCompte({
  ligne,
  lien,
  children,
}: {
  ligne: LigneCompteIa;
  lien: string | null;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
        {ligne.initiales}
      </span>
      <div className="min-w-0">
        {lien ? (
          <Link
            to={lien}
            className="block truncate font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:leading-10"
          >
            {ligne.prenom} {ligne.nom}
          </Link>
        ) : (
          <div className="truncate font-semibold text-codialis-navy">
            {ligne.prenom} {ligne.nom}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          {ligne.sites.map((code) => (
            <PastilleSite key={code} site={code} />
          ))}
          {!ligne.compteActif && <Pastille texte="Compte inactif" ton="neutral" />}
        </div>
        {children}
        <Detail>
          <span className="break-all">{ligne.email}</span>
        </Detail>
      </div>
    </div>
  );
}

function IdentiteFormateur({ ligne }: { ligne: LigneFormateurIa }): JSX.Element {
  return (
    <IdentiteCompte
      ligne={ligne}
      lien={ligne.formateurId ? `/instructors?formateur=${encodeURIComponent(ligne.formateurId)}` : null}
    />
  );
}

function detailFonctions(parFonction: ParFonctionIa[]): string | null {
  return parFonction.length === 0
    ? null
    : parFonction.map((ligne) => `${ligne.libelle} : ${ligne.envoyees}`).join(' · ');
}

function Requetes({ ligne }: { ligne: LigneCompteIa }): JSX.Element {
  const auDela = ligne.etat === 'AU_DELA_DU_SEUIL' || ligne.etat === 'QUOTA_ATTEINT';
  const fonctions = detailFonctions(ligne.parFonction);
  return (
    <div className="min-w-0 text-xs">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray 2xl:hidden">Requêtes</div>
      <div className={`font-display text-lg font-bold ${auDela ? 'text-amber-600' : 'text-codialis-navy'}`}>
        {ligne.envoyees.jour} <span className="text-xs font-medium text-codialis-gray">aujourd’hui</span>
      </div>
      <Detail>
        7 j : {ligne.envoyees.semaine} · 30 j : {ligne.envoyees.mois}
        {ligne.refusees.mois > 0 && ` · ${ligne.refusees.mois} refusée${ligne.refusees.mois > 1 ? 's' : ''}`}
      </Detail>
      {fonctions && <Detail>{fonctions}</Detail>}
      {ligne.derniereRequete && <Detail>Dernière le {horodatage(ligne.derniereRequete)}</Detail>}
    </div>
  );
}

/** Le quota accordé ; pour un formateur, son seuil d'alerte en dessous. */
function QuotaCompte({
  ligne,
  seuil,
}: {
  ligne: LigneCompteIa;
  seuil?: { seuil: number; seuilPropre: number | null };
}): JSX.Element {
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray 2xl:hidden">Quota</div>
      <div className="font-semibold text-codialis-navy">
        {ligne.quota === null ? 'IA fermée' : `${ligne.quota} requête${ligne.quota > 1 ? 's' : ''} par jour`}
      </div>
      {seuil && (
        <div className="flex flex-wrap items-center gap-1 text-codialis-navy">
          Alerte au-delà de {seuil.seuil}
          {seuil.seuilPropre !== null && <Pastille texte="Propre" ton="info" />}
        </div>
      )}
    </div>
  );
}

function TexteDesactivation({ desactivation }: { desactivation: DesactivationIa }): JSX.Element {
  return (
    <Detail>
      Le {dateReunion(desactivation.le)}
      {desactivation.par ? ` par ${desactivation.par}` : ''} · {desactivation.motif}
    </Detail>
  );
}

function EtatCompte({ ligne, alerteOuverte = false }: { ligne: LigneCompteIa; alerteOuverte?: boolean }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap gap-1">
        <Pastille texte={ligne.etatLibelle} ton={TON_ETAT[ligne.etat]} />
        {alerteOuverte && <Pastille texte="Alerte ouverte" ton="warning" />}
      </div>
      {ligne.desactivation && <TexteDesactivation desactivation={ligne.desactivation} />}
    </div>
  );
}

function BoutonQuota({ ligne, onClick }: { ligne: LigneCompteIa; onClick: () => void }): JSX.Element {
  return (
    <button type="button" onClick={onClick} className={`${CLASSE_GESTE} text-codialis-sky hover:bg-codialis-sky/10`}>
      {ligne.quota === null ? 'Accorder un quota' : 'Quota'}
    </button>
  );
}

function GestesFormateur({
  ligne,
  onGeste,
}: {
  ligne: LigneFormateurIa;
  onGeste: (geste: Geste) => void;
}): JSX.Element {
  const nom = `${ligne.prenom} ${ligne.nom}`;
  return (
    <div className="flex flex-wrap gap-1 2xl:justify-end">
      <BoutonQuota
        ligne={ligne}
        onClick={() => onGeste({ type: 'quota', ligne, formateur: { seuilPropre: ligne.seuilPropre } })}
      />
      <button
        type="button"
        onClick={() =>
          onGeste({
            type: 'activation',
            cible: 'formateurs',
            id: ligne.utilisateurId,
            nom,
            desactiver: !ligne.desactivation,
          })
        }
        className={`${CLASSE_GESTE} ${
          ligne.desactivation ? 'text-codialis-teal hover:bg-codialis-mint/10' : 'text-red-600 hover:bg-red-50'
        }`}
      >
        {ligne.desactivation ? 'Réactiver l’IA' : 'Désactiver l’IA'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Apprenants
// ---------------------------------------------------------------------------

function ListeApprenants({
  data,
  onGeste,
}: {
  data: ReponseSupervisionIa;
  onGeste: (geste: Geste) => void;
}): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [etat, setEtat] = useState<FiltreEtat>('');
  const [formation, setFormation] = useState('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const differee = useValeurDifferee(recherche.trim().toLowerCase(), 200);

  // Formations des apprenants listés, pour le filtre.
  const formations = useMemo(() => {
    const parId = new Map<string, string>();
    for (const ligne of data.apprenants) if (ligne.formation) parId.set(ligne.formation.id, ligne.formation.titre);
    return [...parId].map(([id, titre]) => ({ id, titre })).sort((a, b) => a.titre.localeCompare(b.titre, 'fr'));
  }, [data.apprenants]);

  const lignes = useMemo(() => {
    const sansAccent = (texte: string): string => texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const mots = sansAccent(differee).split(/\s+/).filter(Boolean);
    return data.apprenants.filter((ligne) => {
      if (etat && ligne.etat !== etat) return false;
      if (formation && ligne.formation?.id !== formation) return false;
      const texte = sansAccent(`${ligne.prenom} ${ligne.nom} ${ligne.email}`);
      return mots.every((mot) => texte.includes(mot));
    });
  }, [data.apprenants, etat, formation, differee]);

  useEffect(() => setPage(1), [differee, etat, formation, taillePage]);
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const filtre = Boolean(recherche || etat || formation);

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display font-bold text-codialis-navy">Usage par apprenant</h2>
          <button
            type="button"
            onClick={() => onGeste({ type: 'quotaFormation' })}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            <Users className="h-4 w-4" aria-hidden />
            Quota par formation
          </button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <ChampRecherche
            valeur={recherche}
            onChange={setRecherche}
            etiquette="Rechercher un apprenant"
            placeholder="Nom, prénom ou e-mail…"
          />
          <ChampFiltre valeur={formation} onChange={setFormation} etiquette="Formation">
            <option value="">Toutes les formations</option>
            {formations.map((option) => (
              <option key={option.id} value={option.id}>
                {option.titre}
              </option>
            ))}
          </ChampFiltre>
          <ChampFiltre valeur={etat} onChange={(valeur) => setEtat(valeur as FiltreEtat)} etiquette="État">
            {FILTRES_ETAT_APPRENANT.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          {filtre && (
            <button
              type="button"
              onClick={() => {
                setRecherche('');
                setEtat('');
                setFormation('');
              }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {lignes.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-codialis-gray">
          {data.apprenants.length === 0
            ? 'Aucun compte apprenant dans ce périmètre.'
            : 'Aucun apprenant ne correspond aux filtres.'}
        </p>
      ) : (
        <>
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 2xl:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.utilisateurId} className="space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <IdentiteApprenant ligne={ligne} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Requetes ligne={ligne} />
                  <QuotaCompte ligne={ligne} />
                </div>
                <EtatCompte ligne={ligne} />
                <div className="flex flex-wrap gap-1">
                  <BoutonQuota ligne={ligne} onClick={() => onGeste({ type: 'quota', ligne })} />
                </div>
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto 2xl:block">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Apprenant', 'Requêtes', 'Quota', 'État', ''].map((colonne, index) => (
                    <th key={colonne || index} className={CLASSE_TH}>
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.utilisateurId} className="transition hover:bg-gray-50">
                    <td className={`${CLASSE_TD} max-w-[320px]`}>
                      <IdentiteApprenant ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <Requetes ligne={ligne} />
                    </td>
                    <td className={CLASSE_TD}>
                      <QuotaCompte ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[220px]`}>
                      <EtatCompte ligne={ligne} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <BoutonQuota ligne={ligne} onClick={() => onGeste({ type: 'quota', ligne })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
            total={lignes.length}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="apprenant"
          />
        </>
      )}
    </div>
  );
}

function IdentiteApprenant({ ligne }: { ligne: LigneApprenantIa }): JSX.Element {
  return (
    <IdentiteCompte ligne={ligne} lien={ligne.dossierId ? `/learners/${encodeURIComponent(ligne.dossierId)}` : null}>
      <Detail>
        {ligne.formation ? ligne.formation.titre : 'Sans dossier'}
        {ligne.formation && !ligne.enFormation && ' · pas en formation'}
      </Detail>
    </IdentiteCompte>
  );
}

// ---------------------------------------------------------------------------
// Formations
// ---------------------------------------------------------------------------

function ListeFormations({
  formations,
  fenetre,
  onGeste,
}: {
  formations: LigneFormationIa[];
  fenetre: number;
  onGeste: (geste: Geste) => void;
}): JSX.Element {
  return (
    <Carte
      titre="Usage par formation"
      actions={
        <span className="text-xs text-codialis-gray">
          Tout l’organisme · génération, tuteur des apprenants et correction des tests
        </span>
      }
    >
      {formations.length === 0 ? (
        <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">Aucune formation au catalogue.</p>
      ) : (
        <ul className="grid grid-cols-1 divide-y divide-gray-100 lg:grid-cols-2 lg:divide-y-0">
          {formations.map((formation) => (
            <li
              key={formation.id}
              className="flex flex-col gap-2 border-gray-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:px-6 lg:border-b lg:odd:border-r"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Bot className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
                  <span className="font-semibold text-codialis-navy">{formation.titre}</span>
                  {!formation.active && <Pastille texte="Hors catalogue" ton="neutral" />}
                  {formation.desactivation && <Pastille texte="IA désactivée" ton="danger" />}
                </div>
                <Detail>
                  {formation.niveau} · aujourd’hui : {formation.envoyees.jour} · {fenetre} j : {formation.envoyees.mois}
                  {formation.refuseesMois > 0 &&
                    ` · ${formation.refuseesMois} refusée${formation.refuseesMois > 1 ? 's' : ''}`}
                </Detail>
                {detailFonctions(formation.parFonction) && <Detail>{detailFonctions(formation.parFonction)}</Detail>}
                {formation.desactivation && <TexteDesactivation desactivation={formation.desactivation} />}
              </div>
              <button
                type="button"
                onClick={() =>
                  onGeste({
                    type: 'activation',
                    cible: 'formations',
                    id: formation.id,
                    nom: formation.titre,
                    desactiver: !formation.desactivation,
                  })
                }
                className={`${CLASSE_GESTE} shrink-0 self-start ${
                  formation.desactivation
                    ? 'text-codialis-teal hover:bg-codialis-mint/10'
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                {formation.desactivation ? 'Réactiver l’IA' : 'Désactiver l’IA'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}
